import * as React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Radio,
  RadioGroup,
  Text,
  makeStyles,
} from "@fluentui/react-components";
import type { QueryRelationDefinition } from "../../types.js";

const useStyles = makeStyles({
  content: {
    display: "grid",
    gap: "10px",
  },
  context: {
    color: "var(--colorNeutralForeground3)",
  },
  options: {
    display: "grid",
    gap: "6px",
    maxHeight: "300px",
    overflowY: "auto",
    paddingRight: "4px",
  },
  empty: {
    color: "var(--colorNeutralForeground3)",
  },
});

export interface ManyToOneRelationPickerDialogControlProps {
  open: boolean;
  parentLabel: string;
  attributeLabel: string;
  relations: QueryRelationDefinition[];
  onConfirm: (relationKey: string) => void;
  onClose: () => void;
}

export function ManyToOneRelationPickerDialogControl({
  open,
  parentLabel,
  attributeLabel,
  relations,
  onConfirm,
  onClose,
}: ManyToOneRelationPickerDialogControlProps): JSX.Element {
  const styles = useStyles();
  const [selectedRelationKey, setSelectedRelationKey] = React.useState("");
  const [searchQuery, setSearchQuery] = React.useState("");

  const filteredRelations = React.useMemo(() => {
    const trimmed = searchQuery.trim().toLowerCase();
    if (!trimmed) {
      return relations;
    }

    return relations.filter((relation) => {
      const relationText = `${relation.label} ${relation.targetEntityLogicalName} ${relation.fromAttribute} ${relation.toAttribute}`.toLowerCase();
      return relationText.includes(trimmed);
    });
  }, [relations, searchQuery]);

  React.useEffect(() => {
    if (!open) {
      setSelectedRelationKey("");
      setSearchQuery("");
      return;
    }

    setSelectedRelationKey(relations[0]?.key || "");
  }, [open, relations]);

  React.useEffect(() => {
    if (!filteredRelations.some((relation) => relation.key === selectedRelationKey)) {
      setSelectedRelationKey(filteredRelations[0]?.key || "");
    }
  }, [filteredRelations, selectedRelationKey]);

  return (
    <Dialog
      open={open}
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open) {
          onClose();
        }
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Choose related target</DialogTitle>
          <DialogContent className={styles.content}>
            <Text>Multiple related targets are available for this lookup attribute.</Text>
            <Text className={styles.context}>
              {parentLabel} - {attributeLabel}
            </Text>

            <Input value={searchQuery} onChange={(_event, data) => setSearchQuery(data.value)} placeholder="Filter relations" />

            {filteredRelations.length > 0 ? (
              <RadioGroup value={selectedRelationKey} onChange={(_event, data) => setSelectedRelationKey(data.value)} className={styles.options}>
                {filteredRelations.map((relation) => (
                  <Radio
                    key={relation.key}
                    value={relation.key}
                    label={`${relation.label} (${relation.targetEntityLogicalName}) - ${relation.fromAttribute} -> ${relation.toAttribute}`}
                  />
                ))}
              </RadioGroup>
            ) : (
              <Text className={styles.empty}>No relations match your filter.</Text>
            )}
          </DialogContent>

          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!selectedRelationKey} onClick={() => onConfirm(selectedRelationKey)}>
              Add relation
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}